// ── Sync metadata store ──────────────────────────────────────────────────────
//
// All sync bookkeeping (file-id cache, watermarks, tombstones, dirty set, the
// collection name→uuid map, …) lives behind a small store object so it can be
// pointed at ANY storage backend. Production binds it to `localStorage`; the
// concurrent multi-client tests bind each simulated device to its own in-memory
// backend. Nothing in here talks to the network — it is pure local state.

// ── localStorage keys ─────────────────────────────────────────────────────────
const KEYS = {
  fileIds: "drive_file_ids",
  lastSyncedAt: "last_synced_at",
  pendingSync: "pending_sync_flags",
  bookmarksMeta: "bookmarksMeta",
  bookmarkTombstones: "bookmarkTombstones",
  historyTombstones: "historyTombstones",
  flashcardTombstones: "flashcardTombstones",
  collectionTombstones: "collectionTombstones",
  collectionIdMap: "collection_id_map",
  collectionMeta: "collection_meta", // { [id]: { lastModified } } — collection-level bumps (rename/create)
  dirtyCollections: "dirty_collections", // ids changed locally and not yet confirmed on Drive
  syncedCollectionLM: "synced_collection_lm", // { [id]: lastModified } last seen reconciled
};

// Stable, dependency-free UUID (crypto.randomUUID when available, else v4 fallback).
export const uuid = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const memoryBackend = () => {
  const m = new Map();
  return {
    getItem: (k) => (m.has(k) ? m.get(k) : null),
    setItem: (k, v) => m.set(k, String(v)),
    removeItem: (k) => m.delete(k),
  };
};

const defaultBackend = () => {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    /* SSR / privacy mode */
  }
  return memoryBackend();
};

export const createSyncStore = (backend = defaultBackend(), { mintId = uuid } = {}) => {
  // In-memory write-through cache (B8). If a backend write fails (localStorage
  // quota), the value still lives here for the rest of the session — so a deletion
  // tombstone or a dirty flag that couldn't be persisted is still honoured by this
  // session's syncs and the item does NOT resurrect. `hadWriteError()` lets the sync
  // layer keep retrying / surface the problem. (A cross-restart quota failure is
  // fundamentally unpreventable — nothing can be persisted when storage is full.)
  const cache = new Map();
  let writeError = false;

  const read = (key, fallback) => {
    try {
      const raw = cache.has(key) ? cache.get(key) : backend.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  };

  const write = (key, value) => {
    const raw = JSON.stringify(value);
    cache.set(key, raw); // in-memory truth first — survives a backend quota failure
    try {
      backend.setItem(key, raw);
    } catch {
      writeError = true; // couldn't persist; the cache still holds it this session
    }
  };
  const hadWriteError = () => writeError;

  // ── File ID cache ───────────────────────────────────────────────────────────
  const getFileIdCache = () => read(KEYS.fileIds, {});
  const getFileId = (filename) => getFileIdCache()[filename] ?? null;
  const setFileId = (filename, fileId) => {
    const cache = getFileIdCache();
    cache[filename] = fileId;
    write(KEYS.fileIds, cache);
  };
  const clearFileId = (filename) => {
    const cache = getFileIdCache();
    delete cache[filename];
    write(KEYS.fileIds, cache);
  };
  const clearFileIdCache = () => write(KEYS.fileIds, {});

  // ── Last-synced watermarks (stored as Drive SERVER modifiedTime, epoch ms) ────
  const DEFAULT_SYNCED = { bookmarks: 0, flashcards: 0, history: 0 };
  const getLastSyncedAt = () => ({ ...DEFAULT_SYNCED, ...read(KEYS.lastSyncedAt, {}) });
  const setLastSyncedAt = (type, timestamp) => {
    const current = getLastSyncedAt();
    // Never let the watermark go backwards.
    write(KEYS.lastSyncedAt, { ...current, [type]: Math.max(current[type] || 0, timestamp || 0) });
  };

  // ── Pending sync flags (offline / failure / debounce-not-yet-flushed queue) ───
  const DEFAULT_FLAGS = { bookmarks: false, flashcards: false, history: false };
  const getPendingSyncFlags = () => ({ ...DEFAULT_FLAGS, ...read(KEYS.pendingSync, {}) });
  const setPendingSyncFlag = (type, value) => {
    const flags = getPendingSyncFlags();
    write(KEYS.pendingSync, { ...flags, [type]: value });
  };
  const clearPendingSyncFlag = (type) => setPendingSyncFlag(type, false);
  const hasPendingSync = () => {
    const flags = getPendingSyncFlags();
    return flags.bookmarks || flags.flashcards || flags.history;
  };

  // ── Bookmark metadata ─────────────────────────────────────────────────────────
  const getBookmarksMeta = () => read(KEYS.bookmarksMeta, {});
  const setBookmarkMeta = (word, meta) => {
    const all = getBookmarksMeta();
    write(KEYS.bookmarksMeta, { ...all, [word]: meta });
  };
  const deleteBookmarkMeta = (word) => {
    const all = getBookmarksMeta();
    delete all[word];
    write(KEYS.bookmarksMeta, all);
  };
  const setBookmarksMeta = (metaMap) => write(KEYS.bookmarksMeta, metaMap || {});
  const bulkSetBookmarksMeta = (metaMap) => {
    const existing = getBookmarksMeta();
    write(KEYS.bookmarksMeta, { ...existing, ...metaMap });
  };

  // ── Bookmark / collection tombstones (flat: { [id]: deletedAt }) ──────────────
  const getBookmarkTombstones = () => read(KEYS.bookmarkTombstones, {});
  const addBookmarkTombstone = (word, ts = Date.now()) => {
    const t = getBookmarkTombstones();
    write(KEYS.bookmarkTombstones, { ...t, [word]: ts });
  };
  const setBookmarkTombstones = (map) => write(KEYS.bookmarkTombstones, map || {});

  // ── History tombstones (flat: { [word]: deletedAt }) ──────────────────────────
  const getHistoryTombstones = () => read(KEYS.historyTombstones, {});
  const addHistoryTombstone = (word, ts = Date.now()) => {
    const t = getHistoryTombstones();
    if (!t[word] || ts > t[word]) write(KEYS.historyTombstones, { ...t, [word]: ts });
  };
  const setHistoryTombstones = (map) => write(KEYS.historyTombstones, map || {});

  const getCollectionTombstones = () => read(KEYS.collectionTombstones, {});
  const addCollectionTombstone = (collectionId, ts = Date.now()) => {
    const t = getCollectionTombstones();
    write(KEYS.collectionTombstones, { ...t, [collectionId]: ts });
  };
  const setCollectionTombstones = (map) => write(KEYS.collectionTombstones, map || {});

  // ── Flashcard tombstones (scoped: { [collectionId]: { [wordID]: deletedAt } }) ─
  // Legacy data may be flat { [wordID]: ts }; those are migrated into a "__global"
  // bucket so old deletions keep working while new ones are collection-scoped.
  const GLOBAL = "__global";
  const getFlashcardTombstones = () => {
    const raw = read(KEYS.flashcardTombstones, {});
    // Detect & migrate legacy flat shape (values are numbers).
    const looksFlat = Object.values(raw).some((v) => typeof v === "number");
    if (looksFlat) {
      const migrated = { [GLOBAL]: {} };
      for (const [k, v] of Object.entries(raw)) {
        if (typeof v === "number") migrated[GLOBAL][k] = v;
        else migrated[k] = v;
      }
      write(KEYS.flashcardTombstones, migrated);
      return migrated;
    }
    return raw;
  };
  const addFlashcardTombstone = (collectionId, wordID, ts = Date.now()) => {
    const all = getFlashcardTombstones();
    const bucket = { ...(all[collectionId] || {}), [wordID]: ts };
    write(KEYS.flashcardTombstones, { ...all, [collectionId]: bucket });
  };
  // Effective tombstone for a card: max of its collection bucket and the legacy global bucket.
  const getFlashcardDeletedAt = (collectionId, wordID, all = getFlashcardTombstones()) => {
    const scoped = all[collectionId]?.[wordID] ?? 0;
    const global = all[GLOBAL]?.[wordID] ?? 0;
    return Math.max(scoped, global);
  };

  // type: 'bookmark' | 'collection'  (flat tombstone maps)
  const bulkSetTombstones = (type, tombstoneMap) => {
    const keyMap = {
      bookmark: KEYS.bookmarkTombstones,
      collection: KEYS.collectionTombstones,
    };
    const key = keyMap[type];
    if (!key) return;
    const existing = read(key, {});
    const merged = { ...existing };
    for (const [id, ts] of Object.entries(tombstoneMap || {})) {
      if (!merged[id] || ts > merged[id]) merged[id] = ts;
    }
    write(key, merged);
  };

  // Authoritative REPLACE of the scoped flashcard tombstone map (prunes stale entries).
  const setFlashcardTombstonesAll = (scopedMap) => write(KEYS.flashcardTombstones, scopedMap || {});

  // Deep-merge scoped flashcard tombstones, keeping the later timestamp per entry.
  const bulkSetFlashcardTombstones = (scopedMap) => {
    const existing = getFlashcardTombstones();
    const merged = { ...existing };
    for (const [collId, bucket] of Object.entries(scopedMap || {})) {
      const target = { ...(merged[collId] || {}) };
      for (const [wid, ts] of Object.entries(bucket || {})) {
        if (!target[wid] || ts > target[wid]) target[wid] = ts;
      }
      merged[collId] = target;
    }
    write(KEYS.flashcardTombstones, merged);
  };

  // ── Collection ID map (name → uuid) ───────────────────────────────────────────
  const getCollectionIdMap = () => read(KEYS.collectionIdMap, {});
  const getOrCreateCollectionId = (name) => {
    const map = getCollectionIdMap();
    if (Object.prototype.hasOwnProperty.call(map, name) && map[name]) return map[name];
    const id = mintId();
    write(KEYS.collectionIdMap, { ...map, [name]: id });
    return id;
  };
  const renameCollectionId = (oldName, newName) => {
    const map = getCollectionIdMap();
    // Same-name rename would set and then delete the entry, orphaning the id.
    if (oldName === newName || !Object.prototype.hasOwnProperty.call(map, oldName) || !map[oldName]) return;
    const id = map[oldName];
    const updated = { ...map, [newName]: id };
    delete updated[oldName];
    write(KEYS.collectionIdMap, updated);
  };
  const deleteCollectionId = (name) => {
    const map = getCollectionIdMap();
    delete map[name];
    write(KEYS.collectionIdMap, map);
  };
  // Authoritative REPLACE (not spread) — the merged map is canonical, so stale
  // name→id entries (e.g. after a peer rename) are dropped rather than retained.
  const setCollectionIdMap = (idMap) => write(KEYS.collectionIdMap, idMap || {});

  // ── Collection-level metadata (lastModified bumps for rename/create) ──────────
  const getCollectionMeta = () => read(KEYS.collectionMeta, {});
  const getCollectionLM = (id) => getCollectionMeta()[id]?.lastModified ?? 0;
  const setCollectionLM = (id, lastModified) => {
    const all = getCollectionMeta();
    const prev = all[id]?.lastModified ?? 0;
    write(KEYS.collectionMeta, { ...all, [id]: { lastModified: Math.max(prev, lastModified) } });
  };
  const setCollectionMeta = (metaMap) => write(KEYS.collectionMeta, metaMap || {});

  // ── Dirty-collection tracking (drives fast, targeted uploads) ─────────────────
  const getDirtyCollections = () => read(KEYS.dirtyCollections, []);
  const markCollectionDirty = (id) => {
    const set = new Set(getDirtyCollections());
    set.add(id);
    write(KEYS.dirtyCollections, [...set]);
  };
  const clearCollectionDirty = (id) => {
    const set = new Set(getDirtyCollections());
    set.delete(id);
    write(KEYS.dirtyCollections, [...set]);
  };
  const clearAllDirty = () => write(KEYS.dirtyCollections, []);

  // ── Per-collection synced lastModified (remote-change detection) ──────────────
  const getSyncedCollectionLM = () => read(KEYS.syncedCollectionLM, {});
  const setSyncedCollectionLM = (id, lm) => {
    const all = getSyncedCollectionLM();
    write(KEYS.syncedCollectionLM, { ...all, [id]: lm });
  };
  const setSyncedCollectionLMMap = (map) => write(KEYS.syncedCollectionLM, map || {});

  // ── Full reset (called on logout) ─────────────────────────────────────────────
  const clearAllSyncState = () => {
    cache.clear();
    writeError = false;
    Object.values(KEYS).forEach((key) => backend.removeItem(key));
  };

  return {
    GLOBAL,
    hadWriteError,
    getFileIdCache, getFileId, setFileId, clearFileId, clearFileIdCache,
    getLastSyncedAt, setLastSyncedAt,
    getPendingSyncFlags, setPendingSyncFlag, clearPendingSyncFlag, hasPendingSync,
    getBookmarksMeta, setBookmarkMeta, deleteBookmarkMeta, setBookmarksMeta, bulkSetBookmarksMeta,
    getBookmarkTombstones, addBookmarkTombstone, setBookmarkTombstones,
    getHistoryTombstones, addHistoryTombstone, setHistoryTombstones,
    getCollectionTombstones, addCollectionTombstone, setCollectionTombstones,
    getFlashcardTombstones, addFlashcardTombstone, getFlashcardDeletedAt,
    bulkSetTombstones, bulkSetFlashcardTombstones, setFlashcardTombstonesAll,
    getCollectionIdMap, getOrCreateCollectionId, renameCollectionId, deleteCollectionId, setCollectionIdMap,
    getCollectionMeta, getCollectionLM, setCollectionLM, setCollectionMeta,
    getDirtyCollections, markCollectionDirty, clearCollectionDirty, clearAllDirty,
    getSyncedCollectionLM, setSyncedCollectionLM, setSyncedCollectionLMMap,
    clearAllSyncState,
  };
};

// Default singleton bound to localStorage (used by the app; tests build their own).
export const syncStore = createSyncStore();
